/**
 * This file contains the logout template for the settings page.
 */
import Body from '@components/sections/logout/logoutBody';
import Button from '@components/elements/logout/logoutButton';
import CmpUtils from '../../../shared/utils/cmp';
import FlavourConfig from '@shared/config/flavours';
import Focus from '../../../shared/types/enums/focus';
import Container from '../atoms/container/logoutContainer';
import Viewport from '../atoms/container/mainContainer';
import Modals from '@shared/types/enums/modals';
import closeMenu from '../../../shared/actions/menu/closeMenu';
import initAnonymous from '../../../shared/actions/login/initAnonymous';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import useMenu from '../../../shared/components/utils/hooks/useMenu';
import { focusSelector } from '../../../shared/store/slices/focus.slice';
import { magicRemoteSelector } from '../../../shared/store/slices/magicRemote.slice';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { store } from '../../../shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useCallback, useMemo, useState } from 'react';
import AnalyticsInstance from '@shared/utils/analytics';

/**
 * Logout template component
 * Handles the logout functionality and display of the logout modal.
 *
 * @returns {JSX.Element} Logout container with logout information and button
 */
export default function TLogout() {
  // Hooks
  const { props } = useLogout();

  // Render
  return (
    <Viewport id={'logoutContainer'}>
      <Container {...props.container}>
        <Body />
        <Button {...props.button} />
      </Container>
    </Viewport>
  );
}

/**
 * Hook to use the logout functionality and display of the logout modal.
 *
 * @returns {Object} Object with the logout functionality and display of the logout modal
 */
function useLogout() {
  // Dispatch

  const dispatch = useAppDispatch();

  // Selectors

  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);

  // State
  const [displaySpinner, setDisplaySpinner] = useState(false);
  const menuFocused = focus === Focus.MENU;

  // Actions

  const logout = useCallback(() => {
    dispatch(
      setModalParams({
        callback: () => {
          AnalyticsInstance.getOmnitrackManager()?.setLogout();
          AnalyticsInstance.getOmnitrackManager()?.setCustomHeaders(undefined);
          if (FlavourConfig.capabilities.cmp) {
            setDisplaySpinner(true);
            CmpUtils.openBanner(() => {
              initAnonymous(true);
              setDisplaySpinner(false);
            });
          } else {
            initAnonymous(true);
          }
        },
      })
    );
    dispatch(setModal(Modals.LOGOUT));
  }, []);

  // Keyboard hooks

  useBackKeyboard();

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      if (focusSlice.focus !== Focus.MENU && key === 'SELECT') {
        logout();
      }
    },
  });

  // Menu hooks

  useMenu(null, true);

  // Memoized values

  const props = useMemo(() => {
    return {
      button: {
        callback: logout,
        displaySpinner,
        focused: !menuFocused && !enabled,
      },
      container: {
        animation: menuFocused ? 'main-content-out' : 'main-content-in',
        force: true,
        onMouseEnter: closeMenu,
      },
    };
  }, [focus]);

  // Return values

  return {
    props,
  };
}
